import * as Tone from 'tone';
import { MalakaiInstrument } from '../../types';

const pong = new Tone.PingPongDelay('4n', 0.4).toDestination();
pong.wet.value = 0.6;

const lightpong = new Tone.PingPongDelay('8n', 0.01).toDestination();
pong.wet.value = 0.6;

const chorus = new Tone.Chorus(4, 2.5, 0.5).toDestination();
chorus.wet.value = 0.1;

const dist = new Tone.Distortion(0.3).toDestination();
dist.wet.value = 0.5;

const freeverb = new Tone.Freeverb({ roomSize: 0.3 }).toDestination();
freeverb.wet.value = 0.4;

const freeverb2 = new Tone.Freeverb({ roomSize: 0.8 }).toDestination();
freeverb2.wet.value = 0.8;

const feedbackDelay = new Tone.FeedbackDelay('8n', 0.1).toDestination();
feedbackDelay.wet.value = 0.0;

const bigfeedbackDelay = new Tone.FeedbackDelay('4n', 0.8).toDestination();
bigfeedbackDelay.wet.value = 0.8;

export const LightSineSynth: MalakaiInstrument = {
  instrument: () => new Tone.MonoSynth({
    volume: -26,
    portamento: 0.04,
    oscillator: {
      type: 'sine',
    },
    filter: {
      Q: 5,
      type: 'lowpass',
      rolloff: -12,
    },
    envelope: {
      attack: 0.09,
      decay: 0.4,
      sustain: 0.4,
      release: 0.3,
    },
    filterEnvelope: {
      attack: 0.05,
      decay: 0.5,
      sustain: 1,
      release: 0.8,
      baseFrequency: 500,
      octaves: 4,
      exponent: 9,
    },
  }).chain(chorus, pong, freeverb),
  electronic: 1,
};

export const WarmSawBendSynth: MalakaiInstrument = {
  instrument: () => new Tone.MonoSynth({
    volume: -33,
    portamento: 0.04,
    oscillator: {
      type: 'sawtooth',
    },
    filter: {
      Q: 5,
      type: 'lowpass',
      rolloff: -12,
    },
    envelope: {
      attack: 0.7,
      decay: 0.4,
      sustain: 0.4,
      release: 0.3,
    },
    filterEnvelope: {
      attack: 0.05,
      decay: 0.7,
      sustain: 1,
      release: 0.8,
      baseFrequency: 300,
      octaves: 4,
      exponent: 1,
    },
  }).chain(chorus, pong, freeverb2),
  electronic: 1,
};

export const BrassSynth: MalakaiInstrument = {
  instrument: () => new Tone.MonoSynth({
    volume: -26,
    portamento: 0.08,
    oscillator: {
      type: 'sawtooth',
    },
    filter: {
      Q: 2,
      type: 'lowpass',
      rolloff: -24,
    },
    envelope: {
      attack: 0.5,
      decay: 0.1,
      sustain: 0.3,
      release: 0.1,
    },
    filterEnvelope: {
      attack: 0.05,
      decay: 0.8,
      sustain: 0.2,
      release: 1,
      baseFrequency: 1000,
      octaves: 1.5,
    },
  }).chain(chorus),
  electronic: 1,
};

export const MarimbaSynth: MalakaiInstrument = {
  instrument: () => new Tone.MonoSynth({
    volume: -24,
    portamento: 0.04,
    oscillator: {
      type: 'sine',
    },
    filter: {
      Q: 2,
      type: 'lowpass',
      rolloff: -12,
    },
    envelope: {
      attack: 0.005,
      decay: 0.8,
      sustain: 0.001,
      release: 0.9,
    },
    filterEnvelope: {
      attack: 0.1,
      decay: 0.32,
      sustain: 0.9,
      release: 1,
      baseFrequency: 900,
      octaves: 8.3,
    },
  }).chain(pong, freeverb),
  electronic: 1,
};

export const SynthKalimba: MalakaiInstrument = {
  instrument: () => new Tone.FMSynth({
    volume: -15,
    harmonicity: 8,
    modulationIndex: 2,
    oscillator: {
      type: 'sine',
    },
    envelope: {
      attack: 0.001,
      decay: 2,
      sustain: 0.1,
      release: 2,
    },
    modulation: {
      type: 'sine',
    },
    modulationEnvelope: {
      attack: 0.002,
      decay: 0.2,
      sustain: 0,
      release: 0.2,
    },
  }).chain(lightpong, freeverb),
  electronic: 1,
};

export const SynthCello: MalakaiInstrument = {
  instrument: () => new Tone.FMSynth({
    volume: -15,
    portamento: 0.0,
    harmonicity: 3.01,
    modulationIndex: 14,
    oscillator: {
      type: 'triangle',
    },
    envelope: {
      attack: 0.8,
      decay: 0.3,
      sustain: 0.8,
      release: 1.2,
    },
    modulation: {
      type: 'square',
    },
    modulationEnvelope: {
      attack: 0.01,
      decay: 0.5,
      sustain: 0.2,
      release: 0.1,
    },
  }).chain(freeverb2),
  electronic: 1,
};

export const SynthPickBass: MalakaiInstrument = {
  instrument: () => new Tone.Synth({
    oscillator: {
      partials: [
        1,
        3,
        2,
        9,
        8,
      ],
    },
    envelope: {
      attack: 0.001,
      decay: 1.2,
      sustain: 0,
      release: 1.2,
    },
  }).chain(dist),
  electronic: 1,
};

export const SynthXylo: MalakaiInstrument = {
  instrument: () => new Tone.Synth({
    volume: -15,
    oscillator: {
      partials: [
        1,
        0,
        2,
        0,
        3,
      ],
    },
    envelope: {
      attack: 0.001,
      decay: 0.3,
      sustain: 0,
      release: 1.2,
    },
  }).chain(bigfeedbackDelay),
  electronic: 1,
};

export const Arpsawsynth: MalakaiInstrument = {
  instrument: () => new Tone.MonoSynth({
    volume: -46,
    oscillator: {
      type: 'sawtooth',
    },

    filter: {
      Q: 2,
      type: 'lowpass',
      rolloff: -12,
    },

    envelope: {
      attack: 0.0009,
      decay: 0.4,
      sustain: 0.5,
      release: 0.000009,
    },
    filterEnvelope: {
      attack: 0.01,
      decay: 0.002,
      sustain: 0.9,
      release: 0.5,
      baseFrequency: 200,
      octaves: 6,
      exponent: 2,
    },
  }).chain(dist, feedbackDelay, pong, freeverb2),
  electronic: 1,
};
